/* ==========================================================================
   NOIR & OR — add to cart (product page)

   Product cards use Horizon's own 'quick-add' snippet, which routes through
   product-form-component. This file only owns the NOIR product page form.

   It uses Shopify's standard `updateCart` action rather than posting to
   /cart/add.js directly, because the action dispatches
   `shopify:cart:lines-update` — the event Horizon's <cart-drawer-component>
   listens for in order to auto-open the drawer and update the cart bubble.

   Horizon's <cart-items-component> only re-renders itself when that event's
   promise resolves with `detail.sections` HTML attached. The standard action
   does not attach sections, so the drawer can open showing stale (empty)
   markup. We therefore refresh the cart sections ourselves through the
   Section Rendering API once the add has committed.

   Listens in the capture phase and stops propagation, so this is the single
   owner of submit for the NOIR product form.
   ========================================================================== */
(function () {
  'use strict';

  if (window.NoirCart) return;

  var SELECTOR = '.noir-pdp form, form[data-noir-add]';

  /**
   * Re-render every cart-items-component on the page from the server and swap
   * in the fresh markup. Uses the Section Rendering API, so it does not depend
   * on Horizon's internal module graph.
   */
  function refreshCartSections() {
    var nodes = Array.prototype.slice.call(
      document.querySelectorAll('cart-items-component[data-section-id]')
    );
    if (!nodes.length) return Promise.resolve();

    var ids = [];
    nodes.forEach(function (node) {
      var id = node.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (!ids.length) return Promise.resolve();

    var url = window.location.pathname + '?sections=' + encodeURIComponent(ids.join(','));

    return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('section render failed');
        return response.json();
      })
      .then(function (sections) {
        var parser = new DOMParser();

        nodes.forEach(function (node) {
          var html = sections[node.dataset.sectionId];
          if (!html) return;

          var doc = parser.parseFromString(html, 'text/html');
          var fresh =
            doc.querySelector('cart-items-component[data-section-id="' + node.dataset.sectionId + '"]') ||
            doc.querySelector('cart-items-component');

          if (fresh) node.innerHTML = fresh.innerHTML;
        });
      })
      .catch(function () {
        // Leave the drawer as-is rather than blanking it; the cart page is
        // still correct and the next navigation will resync.
      });
  }

  function addToCart(form) {
    var button = form.querySelector('button[name="add"]');
    var label = form.querySelector('[data-atc-label]') || button;
    var original = label ? label.textContent : null;

    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value) return;

    var qtyField = form.querySelector('[name="quantity"]');
    var quantity = qtyField ? Math.max(1, parseInt(qtyField.value, 10) || 1) : 1;

    function settle(text) {
      if (label) label.textContent = text;
      if (button) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
      setTimeout(function () {
        if (label && original) label.textContent = original;
      }, 1800);
    }

    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    if (label) label.textContent = 'Adding...';

    var action = window.Shopify && window.Shopify.actions && window.Shopify.actions.updateCart;

    if (!action) {
      // Storefronts without the standard actions runtime: no drawer will open
      // on its own, so add and navigate to the cart page instead.
      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          window.location.href = '/cart';
        })
        .catch(function () {
          settle('Try again');
        });
      return;
    }

    action(
      { lines: [{ merchandiseId: idField.value, quantity: quantity }] },
      { context: 'product' }
    )
      .then(function (result) {
        // The cart can decline a change without the call itself failing, so
        // userErrors must be checked before reporting success.
        if (result && result.userErrors && result.userErrors.length) {
          settle('Unavailable');
          return;
        }
        return refreshCartSections().then(function () {
          settle('Added');
        });
      })
      .catch(function () {
        settle('Try again');
      });
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches(SELECTOR)) return;

      event.preventDefault();
      event.stopPropagation();
      addToCart(form);
    },
    true
  );

  window.NoirCart = { addToCart: addToCart, refreshCartSections: refreshCartSections };
})();
